import { createTheme } from '@mui/material/styles';

// Design tokens from the Stitch "Executive Precision" design system
// (project 1587770953174176333). Comments give the Stitch token names.
// The site keeps enhanced (AAA, 7:1) text contrast, so two Stitch text colours are swapped
// for the nearest Stitch tone that passes: see accentText and onNavyMuted.
export const colors = {
  primary: '#001026', // primary
  navy: '#0B2545', // primary-container
  secondary: '#1B60A2', // secondary: borders, underlines, large text only (6.2:1)
  accentText: '#00477E', // on-secondary-container: small blue text (7.4:1 or more)
  cyan: '#4CD6FB', // tertiary-fixed-dim: accents on navy
  surface: '#FAF8FF', // surface / background
  surfaceLowest: '#FFFFFF', // surface-container-lowest
  surfaceLow: '#F2F3FF', // surface-container-low
  surfaceContainer: '#EAEDFF', // surface-container
  surfaceHigh: '#E2E7FF', // surface-container-high
  primaryFixed: '#D5E3FF', // primary-fixed
  onSurface: '#131B2E', // on-surface
  onSurfaceVariant: '#44474E', // on-surface-variant
  outline: '#74777F', // outline
  outlineVariant: '#C4C6CF', // outline-variant
  // Stitch's on-primary-container (#778DB2) is 4.6:1 on navy; this lighter tint is 7.9:1.
  onNavyMuted: '#A9BBD6',
  error: '#BA1A1A',
  errorText: '#9D2020', // form error messages (7.5:1)
  focus: '#005596', // keyboard focus rings on light surfaces
  headerBg: 'rgba(250, 248, 255, 0.92)', // sticky header, slightly see-through
  headerBorder: 'rgba(196, 198, 207, 0.3)',
};

export type TokenName = keyof typeof colors;

const cssName = (name: string) => `--stc-${name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

// Components use these CSS variables, so the visitor's contrast choice (font.css,
// html.site-high-contrast) can retune every page without re-rendering. The fallback keeps the
// standard colour if a variable is ever missing.
export const tokens = Object.fromEntries(
  Object.entries(colors).map(([name, value]) => [name, `var(${cssName(name)}, ${value})`])
) as Record<TokenName, string>;

const variables = (values: Partial<Record<TokenName, string>>) =>
  Object.fromEntries(
    Object.entries(values).map(([name, value]) => [cssName(name), value])
  ) as Record<string, string>;

// "High contrast" in Accessibility options: near-black text, darker blue, and borders that
// stand out (at least 4.5:1), on plain white surfaces.
export const highContrastColors: Partial<Record<TokenName, string>> = {
  primary: '#000814',
  secondary: '#00477E',
  accentText: '#00335C',
  surface: '#FFFFFF',
  surfaceLow: '#FFFFFF',
  surfaceContainer: '#F2F3FF',
  surfaceHigh: '#EAEDFF',
  onSurface: '#000814',
  onSurfaceVariant: '#1A1F2B',
  outline: '#3D4048',
  outlineVariant: '#5C5F67',
  onNavyMuted: '#FFFFFF',
};

// "Dark" in Accessibility options: deep navy surfaces with light text. Every text colour is at
// least 7:1 on every surface it is used on; navy bands stay a distinct, lighter navy.
export const darkColors: typeof colors = {
  primary: '#F3F6FF',
  navy: '#14294B',
  secondary: '#86B6EC',
  accentText: '#A6CFFF',
  cyan: '#4CD6FB',
  surface: '#0B1220',
  surfaceLowest: '#121B2C',
  surfaceLow: '#0F1827',
  surfaceContainer: '#1A2538',
  surfaceHigh: '#223049',
  primaryFixed: '#1F3558',
  onSurface: '#E7EBF5',
  onSurfaceVariant: '#C2C8D6',
  outline: '#949AA8',
  outlineVariant: '#56607A',
  onNavyMuted: '#A9BBD6',
  error: '#FFB4AB',
  errorText: '#FFB4AB',
  focus: '#A6CFFF',
  headerBg: 'rgba(11, 18, 32, 0.92)',
  headerBorder: 'rgba(86, 96, 122, 0.6)',
};

export const tokenStyles = {
  ':root': variables(colors),
  'html.site-high-contrast': variables(highContrastColors),
  'html.site-dark': { ...variables(darkColors), colorScheme: 'dark' },
};

const manrope = '"Manrope Variable", sans-serif';
const inter = '"Inter Variable", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
export const mono = '"JetBrains Mono Variable", ui-monospace, Menlo, monospace';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    labelCaps: React.CSSProperties;
    bodyLg: React.CSSProperties;
    labelMd: React.CSSProperties;
    bodySm: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    labelCaps?: React.CSSProperties;
    bodyLg?: React.CSSProperties;
    labelMd?: React.CSSProperties;
    bodySm?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    labelCaps: true;
    bodyLg: true;
    labelMd: true;
    bodySm: true;
  }
}

const mobile = createTheme().breakpoints.down('md');

const makeTheme = (c: typeof colors, mode: 'light' | 'dark') =>
  createTheme({
    typography: {
      fontFamily: inter,
      // display-hero: 56/64, 800, -0.03em; mobile 36/44
      h1: {
        fontFamily: manrope,
        fontWeight: 800,
        fontSize: '3.5rem',
        lineHeight: 64 / 56,
        letterSpacing: '-0.03em',
        color: tokens.onSurface,
        [mobile]: { fontSize: '2.25rem', lineHeight: 44 / 36, letterSpacing: '-0.025em' },
      },
      // headline-xl: 40/48, 700, -0.025em; mobile 28/36
      h2: {
        fontFamily: manrope,
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 48 / 40,
        letterSpacing: '-0.025em',
        color: tokens.onSurface,
        [mobile]: { fontSize: '1.75rem', lineHeight: 36 / 28, letterSpacing: '-0.02em' },
      },
      // headline-lg: 30/38, 700, -0.02em
      h3: {
        fontFamily: manrope,
        fontWeight: 700,
        fontSize: '1.875rem',
        lineHeight: 38 / 30,
        letterSpacing: '-0.02em',
        color: tokens.onSurface,
        [mobile]: { fontSize: '1.5rem', lineHeight: 32 / 24 },
      },
      // headline-md: 24/32, 600, -0.015em
      h4: {
        fontFamily: manrope,
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 32 / 24,
        letterSpacing: '-0.015em',
        color: tokens.onSurface,
      },
      // headline-sm: 18/26, 600, -0.01em
      h5: {
        fontFamily: manrope,
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 26 / 18,
        letterSpacing: '-0.01em',
        color: tokens.onSurface,
      },
      h6: {
        fontFamily: manrope,
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 26 / 18,
        letterSpacing: '-0.01em',
        color: tokens.onSurface,
      },
      subtitle1: { fontFamily: inter, color: tokens.onSurfaceVariant },
      subtitle2: { fontFamily: inter, color: tokens.onSurfaceVariant },
      // body-md: 15/24 (Stitch's default paragraph)
      body1: {
        fontFamily: inter,
        fontSize: '0.9375rem',
        lineHeight: 24 / 15,
        letterSpacing: '-0.005em',
        color: tokens.onSurface,
      },
      // body-md in the muted colour, for supporting copy
      body2: {
        fontFamily: inter,
        fontSize: '0.9375rem',
        lineHeight: 24 / 15,
        letterSpacing: '-0.005em',
        color: tokens.onSurfaceVariant,
      },
      // body-lg: 18/28, for page intros
      bodyLg: {
        fontFamily: inter,
        fontSize: '1.125rem',
        lineHeight: 28 / 18,
        letterSpacing: '-0.01em',
        color: tokens.onSurfaceVariant,
      },
      // body-sm: 13/20
      bodySm: { fontFamily: inter, fontSize: '0.8125rem', lineHeight: 20 / 13 },
      // label-md: 14/20, 500
      labelMd: { fontFamily: inter, fontSize: '0.875rem', lineHeight: 20 / 14, fontWeight: 500 },
      // label-caps: 11/16, 700, 0.08em, uppercase
      labelCaps: {
        fontFamily: inter,
        fontSize: '0.6875rem',
        lineHeight: 16 / 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      button: {
        fontFamily: inter,
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
      },
    },
    palette: {
      mode,
      primary: {
        // Outlined and text buttons use main as their text colour, so dark mode needs a light
        // one; contained buttons keep a navy fill through the override below.
        main: mode === 'dark' ? c.accentText : c.navy,
        light: c.secondary,
        dark: c.primary,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: c.accentText,
        light: c.secondary,
        dark: '#001C38',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#00B4D8',
        light: c.cyan,
        dark: '#007A94',
        contrastText: c.navy,
      },
      error: { main: c.error },
      background: {
        default: c.surface,
        paper: c.surfaceLowest,
      },
      text: {
        primary: c.onSurface,
        secondary: c.onSurfaceVariant,
      },
      divider: c.outlineVariant,
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 20px',
            fontSize: '0.875rem',
            lineHeight: 20 / 14,
          },
          containedPrimary: {
            // In dark mode a brighter navy keeps the button distinct from the page.
            backgroundColor: mode === 'dark' ? '#2A4E85' : tokens.navy,
            '&:hover': { backgroundColor: mode === 'dark' ? '#1F3A66' : colors.primary },
          },
        },
      },
      // MUI lightens dark-mode paper with an overlay; the dark colours already set the surface.
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 2px rgba(19, 27, 46, 0.04)',
            border: `1px solid ${tokens.outlineVariant}`,
          },
        },
      },
    },
  });

const theme = makeTheme(colors, 'light');

// Used when the visitor picks "Dark", so MUI's own parts (form fields, buttons) match.
export const darkTheme = makeTheme(darkColors, 'dark');

export default theme;
